/**
 * As listas que vêm do sistema da empresa, sem React e sem rede.
 *
 * Uma lista é uma operação SQL de listagem já publicada, com as colunas que a
 * Cliqfy escolheu mostrar e o nome de cada uma. Ela é lida na hora, quando
 * alguém pede, e não é guardada no CRM.
 */

import type { ListaDaApi } from '@cliqcrm/contracts';

export type Lista = ListaDaApi;
export type Coluna = Lista['colunas'][number];

/*
 * A data aparece no dia em que foi gravada, e sem a hora.
 *
 * O serviço lê `date`, `timestamp` e `datetime` do banco do cliente como hora
 * de UTC e as manda com `Z`: o dia e a hora gravados estão nas partes de UTC.
 * Converter para o fuso de quem vê mudaria o dia (`2026-10-20` virava 19/10 no
 * Brasil) e a hora (as 08:00 de uma agenda viravam 05:00). Por isso a data é
 * lida em UTC, e a hora fica de fora até o serviço mandar o valor como foi
 * gravado. O texto sem fuso (`2026-10-20` ou `2026-10-20 08:00`) já traz o dia
 * escrito e nem passa por `Date`.
 */
export const FUSO_DAS_DATAS = 'UTC';

const DATA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: FUSO_DAS_DATAS,
});

const DIA_ESCRITO = /^(\d{4})-(\d{2})-(\d{2})(?:[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?$/;

function formatarData(valor: unknown): string {
  const texto = String(valor);
  const partes = DIA_ESCRITO.exec(texto);
  if (partes) return `${partes[3]}/${partes[2]}/${partes[1]}`;
  const d = new Date(texto);
  return Number.isNaN(d.getTime()) ? texto : DATA.format(d);
}

/** O valor de uma célula, pelo tipo da coluna: nulo é traço curto, nunca vazio. */
export function formatarValor(valor: unknown, tipo: Coluna['tipo']): string {
  if (valor === null || valor === undefined || valor === '') return '–';
  if (tipo === 'booleano') return valor === true || valor === 'true' || valor === 1 ? 'Sim' : 'Não';
  if (tipo === 'numero') {
    const n = typeof valor === 'number' ? valor : Number(valor);
    return Number.isFinite(n) ? n.toLocaleString('pt-BR') : String(valor);
  }
  if (tipo === 'data') return formatarData(valor);
  return String(valor);
}

function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/** As linhas em que algum valor mostrado contém o termo, sem caixa nem acento. */
export function filtrarLinhas(
  linhas: readonly Record<string, unknown>[],
  colunas: readonly Coluna[],
  termo: string,
): Record<string, unknown>[] {
  const t = normalizar(termo.trim());
  if (!t) return [...linhas];
  return linhas.filter((l) =>
    colunas.some((c) => normalizar(formatarValor(l[c.nome], c.tipo)).includes(t)),
  );
}

export type Ordem = { coluna: string; direcao: 'asc' | 'desc' } | null;

/** Ordena pela coluna; o vazio vai sempre para o fim, e o texto compara em pt-BR. */
export function ordenarLinhas(
  linhas: readonly Record<string, unknown>[],
  ordem: Ordem,
  colunas: readonly Coluna[],
): Record<string, unknown>[] {
  if (!ordem) return [...linhas];
  const tipo = colunas.find((c) => c.nome === ordem.coluna)?.tipo ?? 'texto';
  const sinal = ordem.direcao === 'asc' ? 1 : -1;
  const vazio = (v: unknown): boolean => v === null || v === undefined || v === '';
  return [...linhas].sort((a, b) => {
    const va = a[ordem.coluna];
    const vb = b[ordem.coluna];
    if (vazio(va) && vazio(vb)) return 0;
    if (vazio(va)) return 1;
    if (vazio(vb)) return -1;
    if (tipo === 'numero') return (Number(va) - Number(vb)) * sinal;
    if (tipo === 'data')
      return (new Date(String(va)).getTime() - new Date(String(vb)).getTime()) * sinal;
    return String(va).localeCompare(String(vb), 'pt-BR') * sinal;
  });
}

/** Clicar no título: sem ordem → crescente → decrescente → sem ordem. */
export function proximaOrdem(atual: Ordem, coluna: string): Ordem {
  if (!atual || atual.coluna !== coluna) return { coluna, direcao: 'asc' };
  return atual.direcao === 'asc' ? { coluna, direcao: 'desc' } : null;
}

/**
 * A "finalidade" da lista: o nome curto pelo qual uma automação a lê. Sai do
 * nome, sem acento, em minúsculas e com _, como o contrato pede.
 */
export function finalidadeDoNome(nome: string): string {
  const base = normalizar(nome)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return base.length >= 2 ? base : 'lista';
}

/** "Rascunho", "Publicada": a situação de uma lista, para a Cliqfy. */
export const SITUACAO_DA_LISTA: Record<Lista['situacao'], string> = {
  rascunho: 'Em montagem',
  publicada: 'Publicada',
  arquivada: 'Arquivada',
};
